import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, ChevronLeft, Plus, ArrowLeftRight, Loader2, Clock, Dumbbell, AlertTriangle, Check, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import ExerciseDetailModal from '@/components/ExerciseDetailModal';
import { useSlowConnection } from '@/components/SmartVideo';

// Equipment icon mapping using simple visual cues
const EQUIPMENT_CONFIG = {
  dumbbell:    { label: 'Dumbbell',    icon: '🏋️' },
  barbell:     { label: 'Barbell',     icon: '🏋️' },
  bodyweight:  { label: 'Bodyweight',  icon: '🤸' },
  cable:       { label: 'Cable',       icon: '🔗' },
  kettlebell:  { label: 'Kettlebell',  icon: '⚡' },
  lever:       { label: 'Machine',     icon: '⚙️' },
  machine:     { label: 'Machine',     icon: '⚙️' },
  band:        { label: 'Band',        icon: '〰️' },
  rings:       { label: 'Rings',       icon: '⭕' },
  smith:       { label: 'Smith',       icon: '🔩' },
  weighted:    { label: 'Plates',    icon: '🎯' },
  suspension:  { label: 'Suspension',  icon: '📐' },
  ez_bar:      { label: 'EZ Bar',      icon: '🏋️' },
  landmine:    { label: 'Landmine',    icon: '💥' },
  sled:        { label: 'Sled',        icon: '🛷' },
  trap_bar:    { label: 'Trap Bar',    icon: '🏋️' },
  battle_rope: { label: 'Battle Rope', icon: '🌊' },
  stick:       { label: 'Stick',       icon: '📏' },
  medicine_ball: { label: 'Med Ball',  icon: '🏐' },
};

const MUSCLE_COLORS = {
  chest: 'text-red-400',
  back: 'text-blue-400',
  shoulders: 'text-orange-400',
  biceps: 'text-purple-400',
  triceps: 'text-pink-400',
  quads: 'text-green-400',
  hamstrings: 'text-emerald-400',
  glutes: 'text-amber-400',
  core: 'text-yellow-400',
  calves: 'text-teal-400',
  lats: 'text-indigo-400',
  rear_delts: 'text-rose-400',
  forearms: 'text-lime-400',
  lower_back: 'text-cyan-400',
};

// Muscle group card images — each unique, matching the muscle
const MUSCLE_CARD_STYLES = {
  back: { img: '/muscle-back.jpg?v=2' },
  quads: { img: '/muscle-quads.jpg?v=2' },
  chest: { img: '/muscle-chest.jpg?v=3' },
  core: { img: '/muscle-core.jpg?v=2' },
  shoulders: { img: '/muscle-shoulders.jpg?v=2' },
  biceps: { img: '/muscle-biceps.jpg?v=3' },
  triceps: { img: '/muscle-triceps.jpg?v=3' },
  glutes: { img: '/muscle-glutes.jpg?v=2' },
  hamstrings: { img: '/muscle-quads.jpg?v=2' },
  calves: { img: '/muscle-calves.jpg?v=2' },
  lats: { img: '/muscle-back.jpg?v=2' },
  forearms: { img: '/muscle-forearms.jpg?v=2' },
  lower_back: { img: '/muscle-back.jpg?v=2' },
  rear_delts: { img: '/muscle-shoulders.jpg?v=2' },
  hip_flexors: { img: '/muscle-quads.jpg?v=2' },
  default: { img: '/muscle-core.jpg?v=2' },
};

// Equipment card images — each unique, showing relevant equipment
const EQUIPMENT_CARD_IMAGES = {
  bodyweight: '/equip-bodyweight.jpg?v=5',
  cable: '/equip-cable.jpg?v=5',
  barbell: '/equip-barbell.jpg?v=5',
  dumbbell: '/equip-dumbbell.jpg?v=5',
  machine: '/equip-machine.jpg?v=5',
  lever: '/equip-machine.jpg?v=5',
  kettlebell: '/equip-kettlebell.jpg?v=5',
  band: '/equip-band.jpg?v=5',
  rings: '/equip-rings.jpg?v=5',
  smith: '/equip-smith.jpg?v=5',
  weighted: '/equip-weighted.jpg?v=5',
  suspension: '/equip-suspension.jpg?v=5',
  ez_bar: '/equip-ez_bar.jpg?v=5',
  landmine: '/equip-landmine.jpg?v=5',
  sled: '/equip-sled.jpg?v=5',
  stick: '/equip-stick.jpg?v=5',
  battle_rope: '/equip-battle_rope.jpg?v=5',
  trap_bar: '/equip-trap_bar.jpg?v=5',
  medicine_ball: '/equip-medicine_ball.jpg?v=6',
  default: '/equip-bodyweight.jpg?v=5',
};


export const ExercisePicker = ({
  isOpen,
  onClose,
  workoutPlanId,
  dayIndex,
  dayType,
  existingExercises = [],
  mode = 'add', // 'add' | 'replace'
  replaceIndex = null,
  onExerciseAdded,
  onExerciseReplaced,
}) => {
  const { api } = useAuth();
  const isSlowConnection = useSlowConnection();

  // State
  const [view, setView] = useState('home'); // 'home' | 'category' | 'muscle' | 'subcategory' | 'search'
  const [categories, setCategories] = useState([]);
  const [muscleGroups, setMuscleGroups] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [categoryExercises, setCategoryExercises] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedMuscle, setSelectedMuscle] = useState(null);
  const [selectedSubLabel, setSelectedSubLabel] = useState(null);
  const pickerScrollRef = useRef(null);
  
  // Scroll to top when view changes
  useEffect(() => {
    if (pickerScrollRef.current) pickerScrollRef.current.scrollTop = 0;
  }, [view]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState({ categories: false, suggestions: false, category: false, search: false, muscles: false });
  const [adding, setAdding] = useState(null);
  const [confirmExercise, setConfirmExercise] = useState(null);
  const [timeImpact, setTimeImpact] = useState(null);
  const [previewExercise, setPreviewExercise] = useState(null); // exercise name for detail preview

  const existingNames = useMemo(
    () => new Set(existingExercises.map(e => (e.exercise || e.name || '').toLowerCase())),
    [existingExercises]
  );

  // Load categories + suggestions on open
  useEffect(() => {
    if (!isOpen) return;
    setView('home');
    setSearchQuery('');
    setConfirmExercise(null);
    setTimeImpact(null);
    loadCategories();
    loadMuscleGroups();
    if (workoutPlanId && dayIndex !== undefined) {
      loadSuggestions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [isOpen, workoutPlanId, dayIndex]);

  const loadCategories = useCallback(async () => {
    setLoading(l => ({ ...l, categories: true }));
    try {
      const res = await api.get('/exercises/by-category');
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    } finally {
      setLoading(l => ({ ...l, categories: false }));
    }
  }, [api]);

  const loadSuggestions = useCallback(async () => {
    setLoading(l => ({ ...l, suggestions: true }));
    try {
      const res = await api.post('/workouts/suggest-for-day', {
        workout_plan_id: workoutPlanId,
        day_index: dayIndex,
      });
      setSuggestions(res.data.suggestions || []);
    } catch (err) {
      console.error('Failed to load suggestions:', err);
    } finally {
      setLoading(l => ({ ...l, suggestions: false }));
    }
  }, [api, workoutPlanId, dayIndex]);

  const loadCategoryExercises = useCallback(async (equipment) => {
    setSelectedCategory(equipment);
    setView('category');
    setLoading(l => ({ ...l, category: true }));
    try {
      const res = await api.get(`/exercises/by-equipment/${equipment}`);
      setCategoryExercises(res.data.exercises || []);
    } catch (err) {
      console.error('Failed to load exercises:', err);
    } finally {
      setLoading(l => ({ ...l, category: false }));
    }
  }, [api]);

  const loadMuscleGroups = useCallback(async () => {
    setLoading(l => ({ ...l, muscles: true }));
    try {
      const res = await api.get('/exercises/by-muscle-group');
      setMuscleGroups(res.data.muscle_groups || []);
    } catch (err) {
      console.error('Failed to load muscle groups:', err);
    } finally {
      setLoading(l => ({ ...l, muscles: false }));
    }
  }, [api]);

  const loadMuscleExercises = useCallback(async (muscle, sub = null) => {
    setSelectedCategory(muscle);
    setSelectedMuscle(muscle);
    
    // If no sub specified, check if this muscle has sub-categories
    if (!sub && ['chest', 'back', 'shoulders'].includes(muscle.toLowerCase())) {
      setLoading(l => ({ ...l, category: true }));
      try {
        const res = await api.get(`/exercises/muscle-subcategories/${muscle}`);
        if (res.data.has_subcategories && res.data.subcategories?.length > 0) {
          setSubcategories(res.data.subcategories);
          setView('subcategory');
          setLoading(l => ({ ...l, category: false }));
          return;
        }
      } catch (err) {
        // Fall through to load all exercises
      }
    }
    
    setView('muscle');
    setLoading(l => ({ ...l, category: true }));
    try {
      const url = sub ? `/exercises/by-muscle/${muscle}?sub=${sub}` : `/exercises/by-muscle/${muscle}`;
      const res = await api.get(url);
      setCategoryExercises(res.data.exercises || []);
    } catch (err) {
      console.error('Failed to load muscle exercises:', err);
    } finally {
      setLoading(l => ({ ...l, category: false }));
    }
  }, [api]);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      if (view === 'search' && !searchQuery) setView('home');
      return;
    }
    setView('search');
    const timer = setTimeout(async () => {
      setLoading(l => ({ ...l, search: true }));
      try {
        const res = await api.get('/exercises', { params: { search: searchQuery } });
        const exercises = res.data.exercises || res.data || [];
        setSearchResults(Array.isArray(exercises) ? exercises : []);
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setLoading(l => ({ ...l, search: false }));
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, api]);

  const handleAddExercise = useCallback(async (exerciseName) => {
    if (mode === 'replace') {
      // For replace mode, use existing replace endpoint
      setAdding(exerciseName);
      try {
        // We use the quick-swap-confirm approach
        const res = await api.post('/workouts/quick-swap-confirm', {
          workout_plan_id: workoutPlanId,
          day_index: dayIndex,
          exercise_index: replaceIndex,
          selected_exercise_name: exerciseName,
        });
        if (res.data.replaced) {
          onExerciseReplaced?.({
            newExercise: res.data.new_exercise,
            exerciseIndex: replaceIndex,
          });
          toast.success(`Replaced with ${exerciseName}`);
          onClose();
        }
      } catch (err) {
        console.error('Replace failed:', err);
        toast.error('Failed to replace exercise');
      } finally {
        setAdding(null);
      }
      return;
    }

    // Add mode — first get time impact
    setAdding(exerciseName);
    try {
      const res = await api.post('/workouts/add-exercise-to-day', {
        workout_plan_id: workoutPlanId,
        day_index: dayIndex,
        exercise_name: exerciseName,
      });
      if (res.data.success) {
        const ti = res.data.time_impact;
        if (ti?.over_target) {
          // Show time warning — but exercise is already added, so we inform user
          toast.success(`Added ${exerciseName}`, { duration: 3000 });
          toast(`Session is now ~${ti.new_total}min (target: ${ti.target_time}min)`, {
            icon: <Clock size={16} />,
            duration: 4000,
          });
        } else {
          toast.success(`Added ${exerciseName} (+${ti?.added_minutes || 0}min)`);
        }
        onExerciseAdded?.(res.data);
        onClose();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to add exercise';
      toast.error(detail);
    } finally {
      setAdding(null);
    }
  }, [api, workoutPlanId, dayIndex, mode, replaceIndex, onExerciseAdded, onExerciseReplaced, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
        data-testid="exercise-picker-overlay"
      >
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          className="absolute inset-0 top-0 bg-background flex flex-col overflow-hidden"
          onClick={e => e.stopPropagation()}
          data-testid="exercise-picker-panel"
        >
          {/* Header */}
          <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-xl border-b border-border" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
            <div className="flex items-center gap-3 px-4 pt-4 pb-3">
              {view !== 'home' ? (
                <button
                  onClick={() => {
                    if (view === 'muscle' && selectedMuscle && ['chest','back','shoulders'].includes(selectedMuscle.toLowerCase())) {
                      setView('subcategory'); // Go back to sub-categories (already loaded)
                    } else if (view === 'subcategory') {
                      setView('home'); setSelectedCategory(null); setSubcategories([]); setSelectedMuscle(null);
                    } else {
                      setView('home'); setSelectedCategory(null); setSearchQuery(''); setSubcategories([]); setSelectedMuscle(null);
                    }
                  }}
                  className="w-9 h-9 rounded-full bg-muted/80 flex items-center justify-center hover:bg-muted transition-colors shrink-0"
                  data-testid="picker-back-btn"
                >
                  <ChevronLeft size={18} />
                </button>
              ) : (
                <button
                  onClick={onClose}
                  className="w-9 h-9 rounded-full bg-muted/80 flex items-center justify-center hover:bg-muted transition-colors shrink-0"
                  data-testid="picker-close-btn"
                >
                  <X size={18} />
                </button>
              )}
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold tracking-tight" data-testid="picker-title">
                  {mode === 'replace' ? 'Replace From Library' : view === 'subcategory' ? (selectedMuscle?.replace('_', ' ') || 'Select Region') : (view === 'category' || view === 'muscle') ? (view === 'muscle' ? (selectedCategory?.replace('_', ' ') || 'Muscle') : (EQUIPMENT_CONFIG[selectedCategory]?.label || selectedCategory)) : 'Add Exercise'}
                </h2>
                {mode === 'replace' && replaceIndex !== null && existingExercises[replaceIndex] && (
                  <p className="text-xs text-muted-foreground truncate">
                    Full library · Replacing: {existingExercises[replaceIndex]?.exercise || existingExercises[replaceIndex]?.name}
                  </p>
                )}
                {mode !== 'replace' && dayType && (
                  <p className="text-xs text-muted-foreground truncate">{dayType}</p>
                )}
              </div>
            </div>

            {/* Search Bar */}
            <div className="px-4 pb-3">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search exercises..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full h-10 pl-9 pr-9 bg-muted/50 border border-border rounded-xl text-sm placeholder:text-muted-foreground focus:outline-none focus:border-accent/40 focus:ring-1 focus:ring-accent/20 transition-all"
                  data-testid="picker-search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto pb-20" ref={pickerScrollRef}>
            {view === 'home' && (
              <HomeView
                suggestions={suggestions}
                categories={categories}
                muscleGroups={muscleGroups}
                loading={loading}
                existingNames={existingNames}
                adding={adding}
                mode={mode}
                onSelectCategory={loadCategoryExercises}
                onSelectMuscle={loadMuscleExercises}
                onAddExercise={handleAddExercise}
                onPreview={setPreviewExercise}
              />
            )}
            {view === 'subcategory' && (
              <div className="px-4 pt-4 space-y-4">
                {/* "Show All" option */}
                <button
                  onClick={() => { setSelectedSubLabel(`All ${selectedMuscle?.replace('_',' ')}`); loadMuscleExercises(selectedMuscle, 'all'); }}
                  className="w-full flex items-center justify-between p-4 rounded-2xl bg-[#1F1F1F] border border-white/10 active:scale-[0.98] transition-transform"
                  data-testid="subcategory-all"
                >
                  <div>
                    <div className="font-heading text-lg uppercase tracking-tight text-white">All {selectedMuscle?.replace('_',' ')}</div>
                    <div className="text-xs text-[#A1A1AA] mt-0.5">Browse everything</div>
                  </div>
                  <ChevronLeft size={16} className="text-[#A1A1AA] rotate-180" />
                </button>
                {/* Sub-category cards */}
                <div className="grid grid-cols-2 gap-3">
                  {subcategories.map((sub, i) => (
                    <button
                      key={sub.id}
                      onClick={() => { setSelectedSubLabel(sub.label); loadMuscleExercises(selectedMuscle, sub.id); }}
                      className="relative overflow-hidden rounded-2xl bg-[#141414] border border-white/10 text-left active:scale-[0.97] transition-transform"
                      data-testid={`subcategory-${sub.id}`}
                    >
                      {sub.image && (
                        <div className="absolute inset-0 bg-gradient-to-br from-[#1A1A1A] to-[#111]">
                          <img src={sub.image} alt="" className="w-full h-full object-cover" loading="eager" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                        </div>
                      )}
                      <div className="relative z-[1] p-5 pt-16">
                        <div className="font-heading text-base uppercase tracking-tight text-white leading-tight">{sub.label}</div>
                        <div className="text-sm font-mono font-bold text-accent mt-1">{sub.count} exercises</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(view === 'category' || view === 'muscle') && (
              <div>
                {/* Sub-category header */}
                {view === 'muscle' && selectedSubLabel && (
                  <div className="px-4 pt-4 pb-2">
                    <h2 className="text-xl font-heading font-black uppercase tracking-tight text-accent">{selectedSubLabel}</h2>
                  </div>
                )}
                <CategoryView
                exercises={categoryExercises}
                loading={loading.category}
                existingNames={existingNames}
                adding={adding}
                mode={mode}
                category={selectedCategory}
                onAddExercise={handleAddExercise}
                onPreview={setPreviewExercise}
              />
              </div>
            )}
            {view === 'search' && (
              <SearchView
                results={searchResults}
                loading={loading.search}
                existingNames={existingNames}
                adding={adding}
                mode={mode}
                query={searchQuery}
                onAddExercise={handleAddExercise}
                onPreview={setPreviewExercise}
              />
            )}
          </div>
        </motion.div>
      </motion.div>

      {/* Exercise Preview Modal */}
      {previewExercise && (
        <ExerciseDetailModal
          exerciseName={previewExercise}
          onClose={() => setPreviewExercise(null)}
        />
      )}
    </AnimatePresence>
  );
};

// ============================================================================
// HOME VIEW
// ============================================================================

function HomeView({ suggestions, categories, muscleGroups, loading, existingNames, adding, mode, onSelectCategory, onSelectMuscle, onAddExercise, onPreview }) {
  return (
    <div className="space-y-6 pt-4">
      {/* Suggested Exercises */}
      {suggestions.length > 0 && (
        <section className="px-4" data-testid="suggestions-section">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-5 rounded-full bg-accent" />
            <h3 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
              Suggested for this day
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {suggestions.map((ex, i) => (
              <ExerciseRow
                key={ex.name}
                exercise={ex}
                isExisting={existingNames.has(ex.name?.toLowerCase())}
                adding={adding}
                mode={mode}
                onAdd={onAddExercise}
                onPreview={onPreview}
                isSuggestion
                index={i}
              />
            ))}
          </div>
        </section>
      )}

      {loading.suggestions && suggestions.length === 0 && (
        <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground text-sm">
          <Loader2 size={16} className="animate-spin" />
          Loading suggestions...
        </div>
      )}

      {/* Equipment Categories */}
      <section className="px-4" data-testid="categories-section">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1.5 h-5 rounded-full bg-accent" />
          <h3 className="text-sm font-bold tracking-wide uppercase text-muted-foreground">Browse by Equipment</h3>
        </div>

        {loading.categories ? (
          <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground text-sm">
            <Loader2 size={16} className="animate-spin" /> Loading...
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3" data-testid="category-grid">
            {categories.map((cat, i) => {
              const config = EQUIPMENT_CONFIG[cat.equipment] || { label: cat.equipment, icon: '🏋️' };
              return (
                <motion.button
                  key={cat.equipment}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.025 }}
                  onClick={() => onSelectCategory(cat.equipment)}
                  className="group relative overflow-hidden rounded-2xl text-left active:scale-[0.97] transition-transform"
                  data-testid={`category-card-${cat.equipment}`}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[#1A1A1A] to-[#111]">
                    <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/[0.03] to-transparent" />
                    <img src={EQUIPMENT_CARD_IMAGES[cat.equipment] || EQUIPMENT_CARD_IMAGES.default} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="eager" decoding="async" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  </div>
                  <div className="relative z-[1] p-5 pt-28">
                    <div className="font-heading font-black text-2xl uppercase tracking-tight text-white">{config.label}</div>
                    <div className="text-sm text-accent font-mono font-bold mt-0.5">{cat.count} exercises</div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </section>

      {/* Muscle Group Categories */}
      <section className="px-4" data-testid="muscle-groups-section">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1.5 h-5 rounded-full bg-accent" />
          <h3 className="text-sm font-bold tracking-wide uppercase text-muted-foreground">Browse by Muscle</h3>
        </div>

        {loading.muscles ? (
          <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground text-sm">
            <Loader2 size={16} className="animate-spin" /> Loading...
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3" data-testid="muscle-group-grid">
            {muscleGroups.map((mg, i) => {
              const cardStyle = MUSCLE_CARD_STYLES[mg.muscle] || MUSCLE_CARD_STYLES.default;
              return (
                <motion.button
                  key={mg.muscle}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.025 }}
                  onClick={() => onSelectMuscle(mg.muscle)}
                  className="group relative overflow-hidden rounded-2xl text-left active:scale-[0.97] transition-transform"
                  data-testid={`muscle-card-${mg.muscle}`}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[#1A1A1A] to-[#111]">
                    <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/[0.03] to-transparent" />
                    <img src={cardStyle.img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="eager" decoding="async" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  </div>
                  <div className="relative z-[1] p-5 pt-28">
                    <div className="font-heading font-black text-2xl uppercase tracking-tight text-white capitalize">{mg.muscle.replace('_', ' ')}</div>
                    <div className="text-sm text-accent font-mono font-bold mt-0.5">{mg.count} exercises</div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

// ============================================================================
// CATEGORY VIEW
// ============================================================================

function CategoryView({ exercises, loading, existingNames, adding, mode, category, onAddExercise, onPreview }) {
  const [activeMuscle, setActiveMuscle] = useState('all');

  // Get unique muscles sorted by count
  const muscles = useMemo(() => {
    const counts = {};
    for (const ex of exercises) {
      const m = ex.primary_muscle || 'other';
      counts[m] = (counts[m] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [exercises]);

  // Filter exercises by selected muscle
  const filtered = useMemo(() => {
    if (activeMuscle === 'all') return exercises;
    return exercises.filter(ex => (ex.primary_muscle || 'other') === activeMuscle);
  }, [exercises, activeMuscle]);

  // Group filtered exercises by muscle
  const grouped = useMemo(() => {
    const groups = {};
    for (const ex of filtered) {
      const muscle = ex.primary_muscle || 'other';
      if (!groups[muscle]) groups[muscle] = [];
      groups[muscle].push(ex);
    }
    return Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
  }, [filtered]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-[#A3A3A3] text-sm">
        <Loader2 size={16} className="animate-spin" />
        Loading exercises...
      </div>
    );
  }

  return (
    <div className="pt-2 pb-4">
      {/* Muscle filter pills — horizontal scroll with auto-scroll to active */}
      <div className="px-4 mb-4 overflow-x-auto scrollbar-hide"
        style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x' }}
        ref={el => {
          if (el && !el._scrolled) {
            const active = el.querySelector('[data-active="true"]');
            if (active) { active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' }); el._scrolled = true; }
          }
        }}
      >
        <div className="flex items-center gap-2 w-max">
          <button
            onClick={() => setActiveMuscle('all')}
            data-active={activeMuscle === 'all'}
            className={`px-4 py-2 rounded-xl text-sm font-heading font-bold uppercase tracking-wider whitespace-nowrap transition-all ${activeMuscle === 'all' ? 'bg-accent text-accent-foreground' : 'bg-[#1F1F1F] text-[#A3A3A3]'}`}
          >
            All ({exercises.length})
          </button>
          {muscles.map(([muscle, count]) => (
            <button
              key={muscle}
              onClick={() => setActiveMuscle(muscle)}
              data-active={activeMuscle === muscle}
              className={`px-4 py-2 rounded-xl text-sm font-heading font-bold uppercase tracking-wider whitespace-nowrap transition-all ${activeMuscle === muscle ? 'bg-accent text-accent-foreground' : 'bg-[#1F1F1F] text-[#A3A3A3]'}`}
            >
              {muscle.replace('_', ' ')} ({count})
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 mb-3">
        <span className="text-sm text-[#A3A3A3] font-mono">{filtered.length} exercises</span>
      </div>
      {grouped.map(([muscle, exs]) => (
        <div key={muscle} className="mb-5" data-testid={`muscle-group-${muscle}`}>
          {activeMuscle === 'all' && (
            <div className="mx-4 mb-3 flex items-center gap-3">
              <div className={`w-1 h-7 rounded-full ${
                MUSCLE_COLORS[muscle]?.replace('text-', 'bg-') || 'bg-[#A3A3A3]'
              }`} />
              <div className="flex-1 flex items-center justify-between">
                <span className={`text-base font-heading font-black uppercase tracking-wide ${MUSCLE_COLORS[muscle] || 'text-white'}`}>
                  {muscle.replace('_', ' ')}
                </span>
                <span className="text-xs text-[#A3A3A3] font-mono bg-[#1F1F1F] px-2 py-0.5 rounded-md">{exs.length}</span>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2.5 px-4">
            {exs.map((ex, i) => (
              <ExerciseRow
                key={ex.name}
                exercise={ex}
                isExisting={existingNames.has(ex.name?.toLowerCase())}
                adding={adding}
                mode={mode}
                onAdd={onAddExercise}
                onPreview={onPreview}
                index={i}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// SEARCH VIEW
// ============================================================================

function SearchView({ results, loading, existingNames, adding, mode, query, onAddExercise, onPreview }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground text-sm">
        <Loader2 size={16} className="animate-spin" />
        Searching...
      </div>
    );
  }

  if (query && results.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground text-sm">
        <Search size={32} className="mb-3 text-muted-foreground" />
        <p>No exercises found for "{query}"</p>
      </div>
    );
  }

  return (
    <div className="pt-2 px-4">
      <div className="mb-2 text-xs text-muted-foreground">{results.length} results</div>
      <div className="grid grid-cols-2 gap-2.5">
      {results.map((ex, i) => (
        <ExerciseRow
          key={ex.name || ex.id || i}
          exercise={ex}
          isExisting={existingNames.has((ex.name || '').toLowerCase())}
          adding={adding}
          mode={mode}
          onAdd={onAddExercise}
          onPreview={onPreview}
          index={i}
        />
      ))}
      </div>
    </div>
  );
}

// ============================================================================
// EXERCISE ROW
// ============================================================================

function ExerciseRow({ exercise, isExisting, adding, mode, onAdd, onPreview, isSuggestion = false, index = 0 }) {
  const isSlowConnection = useSlowConnection();
  const name = exercise.name || exercise.exercise || '';
  const muscle = exercise.primary_muscle || '';
  const equipment = exercise.equipment || '';
  const isAdding = adding === name;

  // Get best available still image
  const thumbnail = exercise.media_urls?.thumbnail || null;
  const img = thumbnail || exercise.images_male?.[0] || exercise.images_female?.[0] || (exercise.media_urls?.images || [])[0] || null;
  const videoUrl = exercise.media_urls?.video || '';

  // Lazy load video poster when card approaches viewport
  const cardRef = useRef(null);
  const [showVideo, setShowVideo] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    if (img || !videoUrl || !cardRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setShowVideo(true); observer.disconnect(); } },
      { rootMargin: '400px' }
    );
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [img, videoUrl]);

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.015, duration: 0.15 }}
      className={`rounded-xl border overflow-hidden transition-all ${
        isSuggestion ? 'bg-accent/5 border-accent/15' : 'bg-card border-border/50'
      } ${isExisting ? 'opacity-40' : ''}`}
      data-testid={`exercise-row-${name.replace(/\s+/g, '-').toLowerCase()}`}
    >
      {/* Image/video poster — tappable */}
      <button
        onClick={() => onPreview?.(name)}
        className="relative w-full aspect-[3/2] bg-gradient-to-br from-[#1A1A1A] to-[#111] flex items-center justify-center overflow-hidden active:opacity-80 transition-opacity"
        data-testid={`preview-btn-${name.replace(/\s+/g, '-').toLowerCase()}`}
      >
        {img ? (
          <img src={img} alt={name} className="w-full h-full object-cover" loading="lazy" />
        ) : showVideo && videoUrl && !isSlowConnection ? (
          <video
            src={`${videoUrl}#t=0.1`}
            className="w-full h-full object-cover"
            preload="metadata"
            muted
            playsInline
          />
        ) : videoUrl ? (
          <div className="w-full h-full bg-[#1A1A1A] animate-pulse" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#1A1A1A] to-[#111] flex flex-col items-center justify-center p-3">
            <Dumbbell size={28} className="text-accent/40 mb-2" />
            <span className="text-[10px] font-heading font-bold uppercase tracking-wider text-white/30 text-center leading-tight">{name}</span>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-8 pointer-events-none" style={{ background: 'linear-gradient(to top, hsl(var(--card)), transparent)' }} />
        <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/50 backdrop-blur-sm">
          <Eye size={9} className="text-white/70" />
        </div>
      </button>

      {/* Info + Action */}
      <div className="p-2 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <button
            onClick={() => onPreview?.(name)}
            className="font-bold text-xs leading-tight text-foreground hover:text-accent transition-colors text-left line-clamp-2 w-full"
          >
            {name}
          </button>
          <div className="flex items-center gap-1.5 mt-0.5">
            {muscle && (
              <span className={`text-xs font-bold uppercase tracking-wider ${MUSCLE_COLORS[muscle] || 'text-muted-foreground'}`}>
                {muscle.replace('_', ' ')}
              </span>
            )}
            {equipment && (
              <span className="text-xs text-muted-foreground font-medium capitalize">
                {EQUIPMENT_CONFIG[equipment]?.label || equipment}
              </span>
            )}
          </div>
        </div>
        {isExisting ? (
          <span className="text-xs text-muted-foreground shrink-0">Added</span>
        ) : (
          <Button
            size="sm"
            disabled={isAdding || !!adding}
            onClick={() => onAdd(name)}
            className="h-7 px-2.5 rounded-lg text-xs font-bold shrink-0 transition-all active:scale-95 bg-accent hover:bg-accent/90 text-accent-foreground"
            data-testid={`add-exercise-btn-${name.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {isAdding ? (
              <Loader2 size={10} className="animate-spin" />
            ) : mode === 'replace' ? (
              <><ArrowLeftRight size={10} className="mr-0.5" /> Swap</>
            ) : (
              <><Plus size={10} className="mr-0.5" /> Add</>
            )}
          </Button>
        )}
      </div>
    </motion.div>
  );
}

export default ExercisePicker;
